import { useState, useRef, useEffect } from "react";
import { Send, Loader2 } from "lucide-react";
import { ListingCard } from "@/components/ListingCard";
import api from "@/lib/api";

const GREETING = "Awright, I'm Pete. Just tell me what you're after, like a diesel van, a house to rent or a bike for the kids, and I'll dig out the best deals on DealShed for you.";

const SUGGESTIONS = [
  "Reliable diesel van in Cork under €25k",
  "3-bed house to rent in Galway",
  "Kids' bike under €100",
  "Farm machinery in Tipperary",
];

export function PeteChat({ initialQuery }) {
  const [messages, setMessages] = useState([{ role: "pete", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    // Send the recent conversation so Pete can handle refinements across turns.
    const history = messages.map((m) => ({ role: m.role === "user" ? "user" : "pete", text: m.text }));
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/pete/chat", { query: q, history });
      setMessages((m) => [...m, { role: "pete", text: data.reply, listings: data.listings || [], count: data.count }]);
    } catch {
      setMessages((m) => [...m, { role: "pete", text: "Sorry, I couldn't have a look just now. Give it another go in a sec." }]);
    } finally {
      setLoading(false);
    }
  };

  const autoSent = useRef(false);
  useEffect(() => {
    if (initialQuery && !autoSent.current) {
      autoSent.current = true;
      send(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  return (
    <div className="bg-white border border-emerald-200 rounded-2xl shadow-sm overflow-hidden max-w-3xl mx-auto text-left" data-testid="pete-chat">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-600 text-white">
        <img src="/pete-avatar.png" alt="Pete, the DealShed market trader" data-testid="pete-avatar-img"
          className="w-10 h-10 rounded-full object-cover bg-white/20 ring-2 ring-white/40" />
        <div>
          <p className="font-heading font-semibold leading-tight">Pete</p>
          <p className="text-xs text-emerald-50/90">Your DealShed market trader</p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-emerald-50">
          <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" /> Online
        </span>
      </div>

      {/* Thread */}
      <div ref={threadRef} className="max-h-[380px] overflow-y-auto px-4 py-4 space-y-4 bg-slate-50/60" data-testid="pete-thread">
        {messages.map((m, i) => (
          <div key={i}>
            {m.role === "user" ? (
              <div className="flex justify-end">
                <div data-testid="pete-message-user" className="bg-emerald-600 text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%] text-sm">{m.text}</div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5">
                <img src="/pete-avatar.png" alt="" className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5" />
                <div className="max-w-[90%]">
                  <div data-testid="pete-message-pete" className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-slate-800">{m.text}</div>
                  {m.listings && m.listings.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pete-listings">
                      {m.listings.slice(0, 6).map((l) => <ListingCard key={l.id} listing={l} />)}
                    </div>
                  )}
                  {m.listings && m.listings.length === 0 && m.count === 0 && (
                    <p className="text-xs text-slate-400 mt-1.5">No matches on the stall just now. Try describing it a different way.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2.5 text-slate-500 text-sm">
            <img src="/pete-avatar.png" alt="" className="w-8 h-8 rounded-full object-cover" />
            <span className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2">
              <Loader2 size={14} className="animate-spin text-emerald-600" /> Pete's having a rummage…
            </span>
          </div>
        )}
      </div>

      {/* Suggestions */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="pete-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid="pete-suggestion-chip" onClick={() => send(s)}
              className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors">{s}</button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 p-3 border-t border-slate-100 bg-white">
        <input data-testid="pete-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tell Pete what you're after…"
          className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-emerald-400 text-slate-900 text-sm" />
        <button data-testid="pete-send-button" onClick={() => send()} disabled={loading || !input.trim()}
          className="grid place-items-center w-11 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white transition-colors">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}
